'use client';

import React, { useState } from 'react';
import { IconChatQuestion, IconCheckCircle } from '@repo/svgs';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/TextArea';
import { ListCell } from '@/components/ui/ListCell';
import { submitAnswerApi } from '@repo/api';
import { useBuilding } from '../../../context/BuildingProvider';
import { useWithAuth } from '@/hooks/useWithAuth';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AnswerQuestionModalProps {
  questionId: string;
  questionText: string;
}

export function AnswerQuestionModal({ questionId, questionText }: AnswerQuestionModalProps) {
  const { building } = useBuilding();
  const withAuth = useWithAuth();
  const analytics = useAnalytics();
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    analytics.track(`${'building' as const}_submit_answer`);
    const words = answer.trim().split(/\s+/);
    if (words.length < 3) {
      setError('Please answer this question with min 3 words.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Auth check + API call + error handling in one line!
      await withAuth(() => submitAnswerApi(questionId, { answer: answer }), {
        title: 'Submit answer',
        description: 'Please log in to submit your answer.',
      });
      analytics.track(`${'building' as const}_answer`);
      setSuccess(true);
    } catch (err) {
      console.error('Error submitting answer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return success ? (
    <div className="flex flex-col gap-6 px-2">
      <IconCheckCircle className="w-8 h-8 text-success" />
      <div className="flex flex-col gap-2">
        <p className="text-body-2-high text-primary">
          Your answer has been submitted for approval.
        </p>
        <p className="text-caption text-secondary">It should be live shortly.</p>
      </div>
    </div>
  ) : (
    <div className="space-y-2">
      <ListCell
        leadingIcon={<IconChatQuestion className="w-6 h-6 icon-secondary" />}
        title={questionText}
        trailing="none"
        className="hover:!bg-white !cursor-default"
      />
      <div className="flex flex-col gap-4 px-2">
        <TextArea
          value={answer}
          onChange={e => {
            setAnswer(e.target.value);
            if (error) setError(null);
          }}
          placeholder={`Answer this question about ${building.primaryAddress.streetAddress}`}
          rows={4}
          alert={!!error}
          helperText={error || 'Min 3 words'}
          className="min-h-[112px]"
        />

        <Button
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          fullWidth
          variant="primary"
        >
          Submit answer
        </Button>
      </div>
    </div>
  );
}
